import React from "react";
import { Link } from "../components/L";
import {
  UserCheck, ShieldCheck, Languages, CalendarClock, Gift, CreditCard,
  Award, Headphones, ClipboardList, Sparkles, Smile, ArrowRight,
  Check, MapPin, Phone, ChevronRight,
} from "lucide-react";
import Seo from "../components/Seo";
import BookingForm from "../components/BookingForm";
import CTABand from "../components/CTABand";
import WhyUs from "../components/WhyUs";
import { IMAGES, cities, contactInfo, cityLabel } from "../mock";
import { useLang } from "../context/LanguageContext";

const iconMap = {
  UserCheck, ShieldCheck, Languages, CalendarClock, Gift, CreditCard,
  Award, Headphones, ClipboardList, Sparkles, Smile,
};

const Home = () => {
  const { lang, t } = useLang();
  const seo = lang === "de"
    ? {
        title: "Putzfrau gesucht? Putzfrau-Service Schweiz | MyCleaner.ch",
        description: "Putzfrau gesucht? MyCleaner.ch stellt geprüfte Putzfrauen & Haushaltshilfe in Zürich, Zug, Luzern, Baden, Genf, Lausanne & der ganzen Schweiz. Fixpreis 43.90 CHF/Std., immer dieselbe Putzfrau, 1. Stunde gratis.",
        keywords: "Putzfrau, Putzfrau Vermittlung, Putzfrau Vermittlung Schweiz, Putzfrau gesucht, Haushaltshilfe, Putzhilfe, Putzfrau Zürich, Putzfrau Zug, Putzfrau Luzern, Putzfrau Baden, Putzfrau Genf, Putzfrau Lausanne, zuverlässige Putzfrau für Privathaushalt, geprüfte Putzfrau stundenweise buchen, Putzfrau mit Fixpreis 43.90 CHF, immer dieselbe Putzfrau buchen, Putzfrau ohne Kreditkarte buchen, günstige Haushaltshilfe Schweiz",
      }
    : {
        title: "Looking for a cleaner? Cleaning service Switzerland | MyCleaner.ch",
        description: "Looking for a cleaner? MyCleaner.ch matches you with vetted cleaners & home help in Zurich, Zug, Lucerne, Baden, Geneva, Lausanne & all of Switzerland. Flat rate CHF 43.90/hr, always the same cleaner, 1st hour free.",
        keywords: "cleaner, cleaner placement, home help, house cleaner Switzerland, cleaner Zurich, cleaner Zug, cleaner Lucerne, cleaner Geneva, cleaner Lausanne",
      };

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: "MyCleaner.ch",
    description: seo.description,
    areaServed: "Switzerland",
    telephone: contactInfo.phone,
    priceRange: "CHF 43.90/hr",
  };

  return (
    <div className="overflow-hidden">
      <Seo pageKey="home" title={seo.title} description={seo.description} keywords={seo.keywords} jsonLd={jsonLd} />

      {/* HERO */}
      <section className="relative pt-[92px] md:pt-[116px] pb-16 bg-gradient-to-b from-amber-50 via-teal-50/40 to-white">
        <div className="max-w-[1200px] mx-auto px-5 md:px-6 grid lg:grid-cols-2 gap-12 items-center">
          <div className="animate-fade-up">
            <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-800 font-semibold text-sm px-4 py-1.5 rounded-full mb-5">
              <Gift className="w-4 h-4" /> {t.hero.badge}
            </div>
            <h1 className="font-head font-extrabold text-4xl md:text-[52px] leading-[1.08] text-slate-900 mb-5">
              {t.hero.titleTop} <span className="text-[#1a7fc4]">{t.hero.titleAccent}</span>
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed mb-7 max-w-xl">{t.hero.subtitle}</p>
            <div className="flex flex-wrap gap-3 mb-8">
              <Link to="/buchen" className="inline-flex items-center gap-2 bg-teal-700 text-white font-head font-bold text-[17px] px-7 py-4 rounded-xl hover:bg-teal-800 transition-colors shadow-lg shadow-teal-800/20">
                {t.hero.ctaBook} <ArrowRight className="w-5 h-5" />
              </Link>
              <a href={contactInfo.phoneHref} className="inline-flex items-center gap-2 bg-white border border-slate-200 text-slate-800 font-semibold text-[17px] px-7 py-4 rounded-xl hover:border-teal-600 hover:text-teal-700 transition-colors">
                <Phone className="w-5 h-5" /> {contactInfo.phone}
              </a>
            </div>
            <div className="grid grid-cols-2 gap-3 max-w-lg">
              {t.hero.benefits.map((btxt) => (
                <div key={btxt} className="flex items-center gap-2 text-sm text-slate-700">
                  <Check className="w-4 h-4 text-teal-600 shrink-0" /> <span className="font-medium">{btxt}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="relative">
            <div className="rounded-3xl overflow-hidden shadow-2xl">
              <img src={IMAGES.hero} alt={lang === "de" ? "Geprüfte Putzfrau reinigt einen hellen Schweizer Privathaushalt" : "Vetted cleaner tidying a bright Swiss living room"} className="w-full h-[420px] md:h-[520px] object-cover" />
            </div>
            <div className="absolute -bottom-5 -left-2 md:left-6 bg-white rounded-2xl shadow-xl p-4 flex items-center gap-3">
              <span className="w-11 h-11 rounded-xl bg-teal-600 flex items-center justify-center"><ShieldCheck className="w-6 h-6 text-white" /></span>
              <div data-testid="hero-trust-badge">
                <div className="font-head font-bold text-slate-900 text-sm">{lang === "de" ? "Geprüft & versichert" : "Vetted & insured"}</div>
                <p className="text-xs text-slate-500 mt-0.5">{t.hero.ratingNote}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="bg-teal-700">
        <div className="max-w-[1200px] mx-auto px-5 md:px-6 py-8 grid grid-cols-2 md:grid-cols-4 gap-6">
          {t.stats.map((s) => (
            <div key={s.label} className="text-center">
              <div className="font-head font-extrabold text-white text-3xl md:text-4xl">{s.value}</div>
              <div className="text-teal-100 text-sm mt-1">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* TRUST POINTS */}
      <section className="py-20 md:py-24">
        <div className="max-w-[1200px] mx-auto px-5 md:px-6">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <p className="text-teal-600 font-semibold text-sm tracking-wide uppercase mb-3">{t.trustEyebrow}</p>
            <h2 className="font-head font-extrabold text-slate-900 text-3xl md:text-4xl leading-tight">{t.trustTitle}</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {t.trustPoints.map((tp) => {
              const Icon = iconMap[tp.icon];
              return (
                <div key={tp.title} className="bg-white rounded-2xl border border-slate-100 p-7 hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
                  <div className="w-12 h-12 rounded-xl bg-teal-50 flex items-center justify-center mb-5"><Icon className="w-6 h-6 text-teal-700" /></div>
                  <h3 className="font-head font-bold text-slate-900 text-lg mb-2">{tp.title}</h3>
                  <p className="text-slate-600 text-[15px] leading-relaxed">{tp.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SO FUNKTIONIERTS */}
      <section className="py-20 md:py-24 bg-amber-50/50">
        <div className="max-w-[1200px] mx-auto px-5 md:px-6">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <p className="text-teal-600 font-semibold text-sm tracking-wide uppercase mb-3">{t.stepsEyebrow}</p>
            <h2 className="font-head font-extrabold text-slate-900 text-3xl md:text-4xl leading-tight">{t.stepsTitle}</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {t.steps.map((st) => {
              const Icon = iconMap[st.icon];
              return (
                <div key={st.num} className="relative bg-white rounded-2xl p-8 border border-slate-100">
                  <div className="absolute -top-4 left-8 w-9 h-9 rounded-full bg-teal-700 text-white font-head font-bold flex items-center justify-center">{st.num}</div>
                  <div className="w-12 h-12 rounded-xl bg-teal-50 flex items-center justify-center mb-5 mt-3"><Icon className="w-6 h-6 text-teal-700" /></div>
                  <h3 className="font-head font-bold text-slate-900 text-xl mb-2">{st.title}</h3>
                  <p className="text-slate-600 text-[15px] leading-relaxed">{st.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CITIES */}
      <section className="py-20 md:py-24">
        <div className="max-w-[1200px] mx-auto px-5 md:px-6">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <p className="text-teal-600 font-semibold text-sm tracking-wide uppercase mb-3">{t.citiesEyebrow}</p>
            <h2 className="font-head font-extrabold text-slate-900 text-3xl md:text-4xl leading-tight">{t.citiesTitle}</h2>
            <p className="text-slate-600 mt-4">{t.citiesSubtitle}</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {cities.map((c) => (
              <Link key={c.slug} to={`/putzfrau/${c.slug}`} className="group flex items-center justify-between bg-white rounded-xl border border-slate-100 px-5 py-4 hover:border-teal-600 hover:shadow-md transition-all">
                <span className="flex items-center gap-3">
                  <span className="w-10 h-10 rounded-lg bg-teal-50 flex items-center justify-center"><MapPin className="w-5 h-5 text-teal-700" /></span>
                  <span>
                    <span className="block font-head font-bold text-slate-900">{t.term} {cityLabel(c, lang)}</span>
                    <span className="block text-xs text-slate-500">{t.citiesPage.canton} {c.canton} · {t.citiesPage.from}</span>
                  </span>
                </span>
                <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-teal-600 transition-colors" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT WE CLEAN */}
      <section className="py-20 md:py-24 bg-teal-50/60">
        <div className="max-w-[1200px] mx-auto px-5 md:px-6 grid lg:grid-cols-2 gap-12 items-center">
          <div className="grid grid-cols-2 gap-4">
            <img src={IMAGES.living} alt={lang === "de" ? "Aufgeräumtes helles Wohnzimmer" : "Tidy bright living room"} className="rounded-2xl h-56 w-full object-cover shadow-md" />
            <img src={IMAGES.kitchen} alt={lang === "de" ? "Blitzsaubere Küche" : "Spotless kitchen"} className="rounded-2xl h-56 w-full object-cover shadow-md mt-8" />
            <img src={IMAGES.bathroom} alt={lang === "de" ? "Glänzend sauberes Badezimmer" : "Sparkling bathroom"} className="rounded-2xl h-56 w-full object-cover shadow-md" />
            <img src={IMAGES.supplies} alt={lang === "de" ? "Professionelle Reinigungsutensilien" : "Professional cleaning supplies"} className="rounded-2xl h-56 w-full object-cover shadow-md mt-8" />
          </div>
          <div>
            <p className="text-teal-600 font-semibold text-sm tracking-wide uppercase mb-3">{t.cleanEyebrow}</p>
            <h2 className="font-head font-extrabold text-slate-900 text-3xl md:text-4xl leading-tight mb-5">{t.cleanTitle}</h2>
            <p className="text-slate-600 leading-relaxed mb-7">{t.cleanDesc}</p>
            <div className="grid grid-cols-2 gap-3">
              {t.tasks.map((tk) => (
                <div key={tk.id} className="flex items-center gap-2.5 text-slate-700">
                  <span className="w-6 h-6 rounded-full bg-teal-600 flex items-center justify-center shrink-0"><Check className="w-3.5 h-3.5 text-white" /></span>
                  <span className="text-[15px]">{tk.label}</span>
                </div>
              ))}
            </div>
            <Link to="/buchen" className="inline-flex items-center gap-2 mt-8 text-teal-700 font-head font-bold hover:gap-3 transition-all">{t.cleanCta} <ArrowRight className="w-5 h-5" /></Link>
          </div>
        </div>
      </section>

      {/* PERKS */}
      <section className="py-20 md:py-24">
        <div className="max-w-[1200px] mx-auto px-5 md:px-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {t.perks.map((p) => {
            const Icon = iconMap[p.icon];
            return (
              <div key={p.title} className="text-center px-4">
                <div className="w-14 h-14 rounded-2xl bg-amber-50 flex items-center justify-center mx-auto mb-4"><Icon className="w-7 h-7 text-amber-500" /></div>
                <h3 className="font-head font-bold text-slate-900 text-lg mb-2">{p.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{p.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* BOOKING FORM */}
      <section id="buchen" className="py-20 md:py-24 bg-white">
        <div className="max-w-[1200px] mx-auto px-5 md:px-6 grid lg:grid-cols-2 gap-12 items-start">
          <div className="lg:pt-6 flex flex-col gap-8">
            <div>
              <p className="text-teal-600 font-semibold text-sm tracking-wide uppercase mb-3">{t.bookingSection.eyebrow}</p>
              <h2 className="font-head font-extrabold text-slate-900 text-3xl md:text-4xl leading-tight mb-5">{t.bookingSection.title}</h2>
              <p className="text-slate-600 leading-relaxed">{t.bookingSection.desc}</p>
            </div>

            <ul className="space-y-4">
              {t.bookingSection.bullets.map((li) => (
                <li key={li} className="flex items-start gap-3"><span className="w-6 h-6 rounded-full bg-teal-600 flex items-center justify-center shrink-0 mt-0.5"><Check className="w-3.5 h-3.5 text-white" /></span><span className="text-slate-700 text-lg">{li}</span></li>
              ))}
            </ul>

            <div className="mt-6 pt-8 border-t border-slate-100">
              <h3 className="font-head font-bold text-slate-900 text-xl mb-6">{t.whyUs.title}</h3>
              <div className="grid sm:grid-cols-2 gap-x-6 gap-y-7">
                {t.whyUs.items.map((it) => (
                  <div key={it.title} className="flex items-start gap-2.5">
                    <span className="w-6 h-6 rounded-md bg-teal-600 flex items-center justify-center shrink-0 mt-0.5"><Check className="w-3.5 h-3.5 text-white" /></span>
                    <div>
                      <div className="font-semibold text-slate-800 text-sm">{it.title}</div>
                      <div className="text-slate-500 text-[13px] leading-snug">{it.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <BookingForm title={t.booking.titleGratis} />
        </div>
      </section>

      {/* RECRUITING */}
      <section className="py-16 bg-slate-900">
        <div className="max-w-[1200px] mx-auto px-5 md:px-6 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h2 className="font-head font-extrabold text-white text-2xl md:text-3xl mb-2">{t.recruit.title}</h2>
            <p className="text-slate-300 max-w-2xl">{t.recruit.desc}</p>
          </div>
          <Link to="/putzfrau-werden" className="shrink-0 inline-flex items-center gap-2 bg-amber-400 text-slate-900 font-head font-bold px-7 py-4 rounded-xl hover:bg-amber-300 transition-colors">{t.recruit.cta} <ArrowRight className="w-5 h-5" /></Link>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 md:py-24">
        <div className="max-w-[820px] mx-auto px-5 md:px-6">
          <div className="text-center mb-12">
            <p className="text-teal-600 font-semibold text-sm tracking-wide uppercase mb-3">{t.faqEyebrow}</p>
            <h2 className="font-head font-extrabold text-slate-900 text-3xl md:text-4xl">{t.faqTitle}</h2>
          </div>
          <div className="space-y-4">
            {t.faqs.map((f) => (
              <details key={f.q} className="group bg-white rounded-xl border border-slate-100 p-5 open:shadow-md transition-shadow">
                <summary className="flex items-center justify-between cursor-pointer font-head font-semibold text-slate-900 list-none">
                  {f.q}<ChevronRight className="w-5 h-5 text-teal-600 group-open:rotate-90 transition-transform" />
                </summary>
                <p className="text-slate-600 text-[15px] leading-relaxed mt-3">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <CTABand />
    </div>
  );
};

export default Home;
